import { useState } from 'react';
import { Box, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getSigmaTabs, isSigmaPfaTab, type SigmaTab, type SigmaTabFlags } from './sigma-tabs';
import { SigmaIframe } from './SigmaDashboard';

type Props = {
  /** Which optional tabs to show, and the analysis the Patient Flow Analyzer tabs read. */
  config: SigmaTabFlags & { analysisId?: string };
  /**
   * Returns a signed embed URL for a tab. Each app supplies its own, since the signing endpoint differs
   * (chat-api for the chat app, management-api for the editor).
   */
  loadUrl: (tab: SigmaTab) => Promise<string>;
  /** Identifies what is being shown, so a different page doesn't reuse another's URL. */
  cacheKey: readonly unknown[];
};

/**
 * A Sigma dashboard set with its tab bar. A tab's URL is fetched when the tab is shown and not cached: it is signed
 * for one short session, and refetching on window focus would reload the frame under the viewer.
 */
export const SigmaDashboardViewer = ({ config, loadUrl, cacheKey }: Props) => {
  const tabs = getSigmaTabs(config);
  const [selected, setSelected] = useState<SigmaTab>('patient_count');
  // The page may drop the selected tab while it is shown (e.g. it is edited); fall back to the first one.
  const tab = tabs.some((t) => t.key === selected) ? selected : (tabs[0]?.key ?? 'patient_count');

  const needsAnalysis = isSigmaPfaTab(tab) && !config.analysisId;

  const { data: url, isLoading, isError } = useQuery({
    // `cacheKey` identifies the source; `loadUrl` is a new function on every render, so it can't be part of the key.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['sigma-embed-url', ...cacheKey, config, tab],
    queryFn: () => loadUrl(tab),
    enabled: !needsAnalysis,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  const renderBody = () => {
    if (needsAnalysis) {
      return <Message>An analysis ID is required for Patient Flow Analyzer dashboards.</Message>;
    }
    if (isError) {
      return <Message error>Unable to load this dashboard.</Message>;
    }
    if (isLoading || !url) {
      return (
        <Message>
          <CircularProgress size={24} />
        </Message>
      );
    }
    return <SigmaIframe iframeUrl={url} height="100%" />;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', minHeight: 600 }}>
      <Tabs
        value={tab}
        onChange={(_event, value: SigmaTab) => setSelected(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider' }}
      >
        {tabs.map((t) => (
          <Tab key={t.key} value={t.key} label={t.label} />
        ))}
      </Tabs>
      <Box sx={{ flex: 1, minHeight: 0 }}>{renderBody()}</Box>
    </Box>
  );
};

const Message = ({ children, error = false }: { children: React.ReactNode; error?: boolean }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200, p: 2 }}>
    <Typography variant="body2" color={error ? 'error' : 'text.secondary'} component="div">
      {children}
    </Typography>
  </Box>
);
